import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://ratedbite.com'; // Update with your actual domain
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

function SEO({ 
  title = 'RatedBite - Discover the Best Dishes Based on Food Quality',
  description = 'Rate and discover amazing dishes based on food quality only. Find the best pizza, burgers, sushi, and more. Join our community of food lovers who focus on taste, not service.',
  keywords = 'food rating, dish reviews, food quality, restaurant dishes, best food, food discovery, dish recommendations, food community, food photos, dish voting',
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  dish = null
}) {
  const location = useLocation();
  const currentUrl = `${SITE_URL}${location.pathname}`;
  
  const optimizedTitle = title.length > 60 ? title.substring(0, 57) + '...' : title;
  const optimizedDescription = description.length > 160 ? description.substring(0, 157) + '...' : description;

  useEffect(() => {
    // Update document title
    document.title = optimizedTitle;

    // Helper to update or create meta tags
    const updateMetaTag = (name, content, attribute = 'name') => {
      if (!content) return;
      
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const updateLinkTag = (rel, href) => {
      if (!href) return;
      
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Basic meta tags
    updateMetaTag('description', optimizedDescription);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', 'RatedBite');
    
    // Robots
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow, noarchive, nosnippet');
    } else {
      updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }
    
    // Canonical URL
    updateLinkTag('canonical', currentUrl);
    
    // Open Graph tags
    updateMetaTag('og:title', optimizedTitle, 'property');
    updateMetaTag('og:description', optimizedDescription, 'property');
    updateMetaTag('og:image', image, 'property');
    updateMetaTag('og:url', currentUrl, 'property');
    updateMetaTag('og:type', type, 'property');
    updateMetaTag('og:site_name', 'RatedBite', 'property');
    updateMetaTag('og:locale', 'en_US', 'property');
    
    // Twitter Card
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', optimizedTitle);
    updateMetaTag('twitter:description', optimizedDescription);
    updateMetaTag('twitter:image', image);
    
    // Additional SEO tags
    updateMetaTag('theme-color', '#667eea');
    updateMetaTag('format-detection', 'telephone=no');
    updateMetaTag('apple-mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-status-bar-style', 'default');
    updateMetaTag('apple-mobile-web-app-title', 'RatedBite');

    // Structured Data (JSON-LD)
    let structuredData = {
      '@context': 'https://schema.org',
      '@type': type === 'article' ? 'Article' : 'WebSite',
      name: 'RatedBite',
      url: SITE_URL,
      description: optimizedDescription,
      publisher: {
        '@type': 'Organization',
        name: 'RatedBite',
        logo: {
          '@type': 'ImageObject',
          url: `${SITE_URL}/logo.png`
        }
      }
    };

    // Add dish-specific structured data
    if (dish) {
      structuredData = {
        '@context': 'https://schema.org',
        '@type': 'FoodEstablishment',
        name: dish.name,
        description: `Food quality rating for ${dish.name}`,
        image: dish.imageUrl || image,
        aggregateRating: dish.averageRating ? {
          '@type': 'AggregateRating',
          ratingValue: dish.averageRating,
          reviewCount: dish.totalRatings || 0,
          bestRating: 5,
          worstRating: 1
        } : undefined,
        servesCuisine: dish.cuisine,
        address: dish.location ? {
          '@type': 'PostalAddress',
          addressLocality: dish.location
        } : undefined
      };
    }

    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

  }, [optimizedTitle, optimizedDescription, keywords, image, type, currentUrl, dish, noindex]);

  return null;
}

export default SEO;

